import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Select, SvgIcon, Typography } from '@mui/material';
import CalendarPicker from './CalendarPicker';
import {
  ArrowLeft as NavPrevIcon,
  ArrowRight as NavNextIcon
} from 'react-feather';
import { closestDebitDate, endOfMonth, endOfYear, getDateToString, nextDebitDate, startOfHour, startOfMonth, startOfYear } from 'src/utils/date';
import { RESOLUTION } from 'src/constants';
import { useNavigate, useLocation } from 'react-router';
import moment from 'moment';
import { useTheme, useMediaQuery } from "@mui/material";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faCarBolt, faChartColumn, faDroplet, faFireFlameCurved, faTable } from '@fortawesome/pro-solid-svg-icons';
import { COLORS } from 'src/constants';
import ReusableToggle from './ReusableToggle';

const resolutionStrings = {
  hour: "Dag",
  day: 'Dag',
  month: 'Månad',
  year: 'År'
};

const resTrans = ['hour', 'day', 'month', 'year'];

function useQuery(location) {
  return new URLSearchParams(location.search);
}

const DateScrubber = React.memo(
  function DateScrubber({ disableResolution, debitMode, debitInterval = 1, debitStart, customerView = false, moveInDate = null, showType = false, consumption, showToggle = false, showAll = false }) {
    const today = new Date();
    const history = useNavigate();
    const location = useLocation();

    const query = useQuery(location);
    const queryFromDate = query.get('from');
    const queryToDate = query.get('to');
    const queryResolution = query.get('res');
    const querySelected = query.get('s');
    const queryType = query.get('type');
    const queryGraphOrTable = query.get('got')

    //console.log(queryFromDate);

    const [period, setPeriod] = useState({
      from: queryFromDate ? new Date(queryFromDate) : today,
      to: queryToDate ? new Date(queryToDate) : today,
      res: queryResolution || RESOLUTION.DAILY,
      type: queryType,
      graphOrTable: queryGraphOrTable
    });
    const [resolution, setResolution] = useState(resTrans[querySelected] || 'month');
    const [type, setType] = useState(queryType);
    const [graphOrTable, setGraphOrTable] = useState(queryGraphOrTable || 'graph')
    const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
    const [prevButtonDisabled, setPrevButtonDisabled] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const availableTypes = [
      showAll && "all",
      consumption?.coldWaterSensorsExists && "coldwater",
      consumption?.hotWaterSensorsExists && "hotwater",
      consumption?.electicitySensorsExists && "electricity",
      consumption?.heatEnergySensorsExists && "heatenergy",
      consumption?.evChargingSensorsExists && "evcharging"
    ].filter(Boolean);

    useEffect(() => {
      if (debitMode) {
        const nextDate = closestDebitDate(debitStart, queryFromDate, Math.max(debitInterval, 1));
        // console.log({ nextDate, debitStart, queryFromDate, debitInterval, date: nextDate.toDate() });
        handleSetFromDate(nextDate.toDate());
        setInitialized(true);
      }
      else {
        const _fromDate = queryFromDate ? new Date(queryFromDate) : today;
        handleSetFromDate(_fromDate);
        setInitialized(true);
      }
    }, [debitMode]);

    useEffect(() => {
      if (initialized)
        handleSetFromDate(period.from);
    }, [resolution]);

    useEffect(() => {
      if (initialized)
        setHistoryParams();
    }, [period, type, graphOrTable]);

    useEffect(() => {
      setType(queryType)
    }, [queryType]);

    useEffect(() => {
      setGraphOrTable(queryGraphOrTable);
    }, [queryGraphOrTable]);

    /*useEffect(() => {
      console.log(consumption);
    }, [consumption]);*/

    useEffect(() => {
      if ((!type || type === "all" || !availableTypes.includes(type)) && availableTypes.length > 0) {
        setType(availableTypes[0]);  // sätt första tillgängliga
      }
    }, [consumption]);

    const prevPeriod = () => {
      const { from } = period;
      if (debitMode) {
        from.setMonth(from.getMonth() - debitInterval);
      }
      else {
        switch (resolution) {
          // case 'day': from.setDate(from.getDate() - 1); break;
          case 'day':
            from.setDate(from.getDate() - 1);
            break;
          case 'month': from.setMonth(from.getMonth() - 1); break;
          case 'year': from.setFullYear(from.getFullYear() - 1); break;
          case 'hour': {
            const newDate = new Date(from);
            newDate.setDate(from.getDate() - 1);
            newDate.setHours(0, 0, 0, 0);        
            handleSetFromDate(newDate);
            return;
          }
        }
      }
      handleSetFromDate(from);
    };

    const nextPeriod = () => {
      const { from } = period;
      if (debitMode) {
        from.setMonth(from.getMonth() + debitInterval);
      }
      else {
        switch (resolution) {
          // case 'day': from.setDate(from.getDate() + 1); break;
          case 'day':
            from.setDate(from.getDate() + 1);
            break;
          case 'month': from.setMonth(from.getMonth() + 1); break;
          case 'year': from.setFullYear(from.getFullYear() + 1); break;
          case 'hour': {
            const newDate = new Date(from);
            newDate.setDate(from.getDate() + 1); 
            newDate.setHours(0, 0, 0, 0);        
            handleSetFromDate(newDate);
            return;
          }
        }
      }
      handleSetFromDate(from);
    };

    const handleSetToDate = date => {
      if (resolution === 'day') return;
      handleSetPeriod(period.from, date, RESOLUTION.DAILY);
    };

    const handleSetFromDate = _date => {
      if (debitMode) {
        _date.setHours(0);
        const from = moment(_date);
        _date.setMonth(_date.getMonth() + debitInterval - 1);
        const to = moment(endOfMonth(_date));//.add(from.date() - 1, 'days');

        handleSetPeriod(from.toDate(), to.toDate(), RESOLUTION.DAILY);
      }
      else {
        /*const [from, to, res] = {
          day: () => [_date, period.to, RESOLUTION.DAILY],
          month: () => [startOfMonth(_date), endOfMonth(_date), RESOLUTION.DAILY],
          year: () => [startOfYear(_date), endOfYear(_date), RESOLUTION.MONTHLY],
          hour: () => [startOfHour(_date), startOfHour(_date), RESOLUTION.HOUR]
        }[resolution]();*/

        let from, to, res;
        switch (resolution) {
          case 'day':
            from = new Date(_date);
            from.setHours(0, 0, 0, 0);
            //to = period.to;
            to = new Date(from);
            res = RESOLUTION.DAILY;
            break;
          case 'month':
            from = startOfMonth(_date);
            to = endOfMonth(_date);
            res = RESOLUTION.DAILY;
            break;
          case 'year':
            from = startOfYear(_date);
            to = endOfYear(_date);
            res = RESOLUTION.MONTHLY;
            break;
          case 'hour': // egentligen dag
            from = startOfHour(_date);
            to = startOfHour(_date);
            res = RESOLUTION.HOUR;
            break;
        }

        // säkerställ att vi inte går före moveInDate
        if (moveInDate) {
          const minDate = new Date(moveInDate);
          if (from < minDate) from = minDate;

          switch (resolution) {
            case 'month':
              to = endOfMonth(from);
              break;
            case 'year':
              to = endOfYear(from);
              break;
            case 'day':
            case 'hour':
              if (to < from) to = from;
              break;
          }
        }

        handleSetPeriod(from, to, res);
      }
    };

    const handleSetPeriod = (from, to, res) => {
      setPeriod({ from, to, res });
      setNextButtonDisabled(
        to.getTime() >= new Date().getTime()
      );
      if (debitMode)
        setPrevButtonDisabled(
          from.getTime() <= new Date(debitStart).getTime()
        );
    }

    const setHistoryParams = () => {
      const fromString = getDateToString(period.from);
      const toString = getDateToString(period.to);
      const s = resTrans.indexOf(resolution);

      let params = `from=${fromString}&to=${toString}`;
      params += `&res=${period.res}&s=${s}`;
      if(type){
        params += `&type=${type}`;
      } else {
        params += `&type=all`;
      }

      if(graphOrTable){
        params += `&got=${graphOrTable}`;
      } else {
        params += `&got=graph`;
      }
      history(`${location.pathname}?${params}`, { replace: true });
    };

    const handleResolutionChanged = event => setResolution(event.target.value);

    const handleTypeChanged = event => setType(event.target.value);

    const NormalPicker = () => {
      //console.log(period);
      return <>
        <Typography variant="h3" color="textPrimary">
          {getDateToString(period.from)} - {getDateToString(period.to)}
        </Typography>
        {!debitMode &&
          <CalendarPicker
            date={period.from}
            onDateChanged={handleSetFromDate}
            maxDate={today}
            minDate={moveInDate ? new Date(moveInDate) : null}
            resolution={resolution}
          />
        }
      </>
    };

    /*const DayPicker = () => {
      return <>
        <Typography variant="h3" color="textPrimary">
          {getDateToString(period.from)}
        </Typography>
        <CalendarPicker
          date={period.from}
          onDateChanged={handleSetFromDate}
          maxDate={period.to}
          resolution={resolution}
        />
        <Typography variant="h3" color="textPrimary">
          - {getDateToString(period.to)}
        </Typography>
        <CalendarPicker
          date={period.to}
          onDateChanged={handleSetToDate}
          minDate={period.from}
          maxDate={today}
          resolution={resolution}
        />
      </>;
    };*/

    const DayPicker = () => (
      <>
        <Typography variant="h3" color="textPrimary">
          {getDateToString(period.from)}
        </Typography>

        <CalendarPicker
          date={period.from}
          onDateChanged={handleSetFromDate}
          maxDate={today}
          minDate={moveInDate ? new Date(moveInDate) : null}
          resolution={resolution}
        />
      </>
    );

    const NormalControls = () => {
      return <>
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-start" },
          alignItems: "center",
          mt: { xs: 2, sm: 0 }
        }}
      >
        <Button variant={customerView ? "contained" : "text"} sx={{mr: customerView ? 2 : 0}} color="secondary" onClick={prevPeriod} 
        disabled={
          (moveInDate && (() => {
            const prev = new Date(period.from);

            switch (resolution) {
              case 'month':
                prev.setMonth(prev.getMonth() - 1);
                prev.setDate(1); // normalisera till månadens första dag
                break;
              case 'year':
                prev.setFullYear(prev.getFullYear() - 1);
                prev.setMonth(0, 1); // normalisera till årets första dag
                break;
              case 'hour':  // egentligen dag
                prev.setDate(prev.getDate() - 1);
                prev.setHours(0, 0, 0, 0); // normalisera till dagens start
                break;
            }

            const minDate = new Date(moveInDate);
            switch (resolution) {
              case 'month': minDate.setDate(1); break;
              case 'year': minDate.setMonth(0, 1); break;
              case 'hour': minDate.setHours(0, 0, 0, 0); break;
            }

            return prev < minDate;
          })())
          //|| resolution === 'day'
        }>
          <SvgIcon fontSize="small">
            <NavPrevIcon />
          </SvgIcon>
          Föregående {!isMobile && resolutionStrings[resolution]}
        </Button>

        {!nextButtonDisabled && (
          <Button 
          variant={customerView ? "contained" : "text"} 
          color="secondary" 
          onClick={nextPeriod} 
          //disabled={resolution === 'day'} 
          /*sx={{
            mt: { xs: 1, sm: 0 }, // 1 på små (xs), 0 från sm och uppåt
          }}*/
          >
            Nästa {!isMobile && resolutionStrings[resolution]}
            <SvgIcon fontSize="small">
              <NavNextIcon />
            </SvgIcon>
          </Button>
        )}
      </Box>
      </>;
    };

    const DebitControls = () => {
      return <>
        <Button color="secondary" onClick={prevPeriod}>
          <SvgIcon fontSize="small">
            <NavPrevIcon />
          </SvgIcon>
          Föregående period
        </Button>

        {!nextButtonDisabled && (
          <Button color="secondary" onClick={nextPeriod}>
            Nästa period
            <SvgIcon fontSize="small">
              <NavNextIcon />
            </SvgIcon>
          </Button>
        )}
      </>;
    };


    return (
      <div>
        {/*<div style={{ display: 'flex', alignItems: 'center', marginBottom: customerView ? 8 : 0 }}>*/}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },   // gör så att datumet alltid hamnar överst
            alignItems: { xs: "center", sm: "center" }, // centrera på mobil, vänster på desktop
            gap: 1, // lite luft mellan datum och dropdown
            mb: customerView ? 1 : 0
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center"
            }}
          >
          {(resolution === 'day' || resolution === 'hour') && !debitMode
            ? <DayPicker />
            : <NormalPicker />
          }
          </Box>
          {!disableResolution && !debitMode &&
            <Select
              value={resolution}
              onChange={handleResolutionChanged}
            >
              {Object.keys(resolutionStrings)
              .filter(k => !(customerView && k === 'day'))
              .filter(k => customerView || k !== 'hour')
              .map(k => <MenuItem key={k} value={k}>
                {resolutionStrings[k]}
              </MenuItem>)}
            </Select>
          }
          {showType &&
          <Select
            value={type}
            onChange={handleTypeChanged}
          >
            {showAll && <MenuItem value="all">Alla</MenuItem>}
            {consumption?.coldWaterSensorsExists && (
              <MenuItem value="coldwater">
                <FontAwesomeIcon icon={faDroplet} style={{marginRight: 6, color: COLORS.COLDWATER}}/>
                Kallvatten
              </MenuItem>
            )}
            {consumption?.hotWaterSensorsExists && (
              <MenuItem value="hotwater">
                <FontAwesomeIcon icon={faDroplet} style={{marginRight: 6, color: COLORS.HOTWATER}}/>
                Varmvatten
              </MenuItem>
            )}
            {consumption?.electicitySensorsExists && (
              <MenuItem value="electricity">
                <FontAwesomeIcon icon={faBolt} style={{marginRight: 6, color: COLORS.ELECTRICITY}}/>
                El
              </MenuItem>
            )}
            {consumption?.heatEnergySensorsExists && (
              <MenuItem value="heatenergy">
                <FontAwesomeIcon icon={faFireFlameCurved} style={{marginRight: 6, color: COLORS.HEATENERGY}}/>
                Värmeenergi
              </MenuItem>
            )}
            {consumption?.evChargingSensorsExists && (
              <MenuItem value="evcharging">
                <FontAwesomeIcon icon={faCarBolt} style={{marginRight: 6, color: COLORS.EVCHARGING}}/>
                Elbilsladdning
              </MenuItem>
            )}
          </Select>
          }
          {showToggle &&
          <ReusableToggle
            value={graphOrTable}
            onChange={setGraphOrTable}
            leftValue="graph"
            rightValue="table"
            leftIcon={faChartColumn}
            rightIcon={faTable}
            width={100}
            height={44}
          />
          }
        </Box>

        {debitMode
          ? <DebitControls />
          : <NormalControls />
        }

      </div>
    );
  },
  (prevProps, nextProps) => prevProps.fromDate === nextProps.fromDate && prevProps.debitMode === nextProps.debitMode && prevProps.consumption === nextProps.consumption
);

/*DateScrubber.defaultProps = {
  debitInterval: 1
};*/

export default DateScrubber;
